import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { type Observable } from 'rxjs';
import { environment } from '../../../environments/environment';
import { type QuoteLogEntry } from '../models/quote-log-entry';
import { type InsideSalesRep } from '../models/inside-sales-rep';
import { type NsCustomer } from '../models/ns-customer';
import { type NsCustomerFields } from '../models/ns-customer-fields';

import { type EstimateLine } from '../models/estimate-line';
import { type AutoSubmitEstimateResponse } from '../models/auto-submit-estimate';
import {
	type ItemOption,
	type ItemOptionsResponse,
	type QuoteItemMatchesResponse,
	type QuoteItemMatchSaveRow
} from '../models/quote-item-match';
import { type Quote, type ThreadReply } from '../models/quote.model';

@Injectable({
	providedIn: 'root'
})
export class QuoteHttpService {
	private http = inject(HttpClient);

	private quoteLogUrl = `${environment.portalApi.uri}/quote/quotelog`;
	private itemMatchUrl = `${environment.portalApi.uri}/item-match`;

	public getQuoteLog(): Observable<QuoteLogEntry[]> {
		return this.http.get<QuoteLogEntry[]>(this.quoteLogUrl);
	}

	public getQuoteById(id: number): Observable<QuoteLogEntry> {
		return this.http.get<QuoteLogEntry>(`${this.quoteLogUrl}/${id}`);
	}

	public deleteQuoteLogEntries(ids: number[]): Observable<unknown> {
		return this.http.request<unknown>(
			'DELETE',
			`${environment.portalApi.uri}/quote/quotelog`,
			{
				body: ids
			}
		);
	}

	// get the inside sales reps
	public getInsideSalesReps(): Observable<InsideSalesRep[]> {
		return this.http.get<InsideSalesRep[]>(
			`${environment.portalApi.uri}/quote/inside-sales-reps`
		);
	}

	public updateInsideRep(
		quoteId: number,
		insideRepId: string,
		insideRepName: string,
		insideRepEmail?: string
	): Observable<unknown> {
		return this.http.patch<unknown>(
			`${environment.portalApi.uri}/quote/quotelog/${quoteId}/inside-rep`,
			{ insideRepId, insideRepName, insideRepEmail }
		);
	}

	public getCustomers(): Observable<NsCustomer[]> {
		return this.http.get<NsCustomer[]>(
			`${environment.portalApi.uri}/quote/customers`
		);
	}

	// Only the record the user picked is sent — the API works out which id is the parent company and
	// which is the location, and returns the four fields it stored. Blank clears the customer.
	public updateNsCustomer(
		quoteId: number,
		nsRecordId: string
	): Observable<NsCustomerFields> {
		return this.http.patch<NsCustomerFields>(
			`${environment.portalApi.uri}/quote/quotelog/${quoteId}/ns-customer`,
			{ nsRecordId }
		);
	}

	// gets called within commitInsideRepAssignment - once the ISR is commited, the estimate can be submitted
	public autoSubmitEstimate(
		quoteId: number,
		body: {
			existingNsEstimateId?: string;
			customerId: string;
			locationId: string;
			insideRepId: string;
			insideRepEmail?: string;
			insideRepName: string;
			outsideRep: string;
			subject: string;
			dueDate: string | null;
			receivedDate: string;
		}
	): Observable<AutoSubmitEstimateResponse> {
		return this.http.post<AutoSubmitEstimateResponse>(
			`${environment.portalApi.uri}/quote/quotelog/${quoteId}/auto-submit`,
			body
		);
	}

	public getEstimateLines(quoteLogId: number): Observable<EstimateLine[]> {
		return this.http.get<EstimateLine[]>(
			`${environment.portalApi.uri}/quote/quotelog/${quoteLogId}/estimate-lines`
		);
	}

	public syncStatuses(): Observable<unknown> {
		return this.http.post<unknown>(
			`${environment.portalApi.uri}/quote/quotelog/sync-statuses`,
			{}
		);
	}

	public updateDueDate(
		quoteId: number,
		dueDate: string | null
	): Observable<unknown> {
		return this.http.patch<unknown>(
			`${environment.portalApi.uri}/quote/quotelog/${quoteId}/due-date`,
			{ dueDate }
		);
	}

	// Reads the stored match rows. Never runs extraction — if needsExtraction comes
	// back true, call extractItemMatches.
	public getItemMatches(
		quoteId: number
	): Observable<QuoteItemMatchesResponse> {
		return this.http.get<QuoteItemMatchesResponse>(
			`${this.itemMatchUrl}/quote/${quoteId}`
		);
	}

	// Runs extraction and matching, then persists. A POST because it spends model
	// tokens and writes rows. Slow by nature — the caller should show progress.
	public extractItemMatches(
		quoteId: number,
		refresh = false
	): Observable<QuoteItemMatchesResponse> {
		return this.http.post<QuoteItemMatchesResponse>(
			`${this.itemMatchUrl}/quote/${quoteId}/extract`,
			{},
			{ params: new HttpParams().set('refresh', refresh) }
		);
	}

	public getItemOptions(quoteId: number): Observable<ItemOptionsResponse> {
		return this.http.get<ItemOptionsResponse>(
			`${this.itemMatchUrl}/quote/${quoteId}/options`
		);
	}

	// Scoped to the quote, so a market basket customer is only ever offered items
	// from their own basket.
	public searchItems(
		quoteId: number,
		query: string,
		take = 50
	): Observable<ItemOption[]> {
		const params = new HttpParams()
			.set('quoteId', quoteId)
			.set('q', query)
			.set('take', take);

		return this.http.get<ItemOption[]>(
			`${this.itemMatchUrl}/items/search`,
			{
				params
			}
		);
	}

	public saveItemMatches(
		quoteId: number,
		rows: QuoteItemMatchSaveRow[]
	): Observable<QuoteItemMatchesResponse> {
		return this.http.put<QuoteItemMatchesResponse>(
			`${this.itemMatchUrl}/quote/${quoteId}`,
			{ rows }
		);
	}

	public getQuoteThreadReplies(quote: Quote): Observable<ThreadReply[]> {
		return this.http.get<ThreadReply[]>(
			`${this.quoteLogUrl}/${quote.id}/thread-replies`
		);
	}
}
